/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2019, 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([],() => {
	const valueSlotIds = ['values', 'x', 'y'];
	const validateSlotAPI = (slotAPI) => {
		if (!slotAPI) {
			throw new Error('Invalid SlotAPI');
		}
	};

	class SlotAPIUtil {

		static get MULTI_MEASURES_SERIES() {
			return '_multiMeasuresSeries';
		}

		static get MULTI_MEASURES_VALUE() {
			return '_multiMeasuresValue';
		}

		static isValuesSlot (slotAPI) {
			validateSlotAPI(slotAPI);
			return valueSlotIds.indexOf(slotAPI.getId()) !== -1;
		}

		static isMultiMeasureSeriesDataItem(slotAPI, index) {
			return slotAPI.getDataItemList()[index].getColumnId() === SlotAPIUtil.MULTI_MEASURES_SERIES;
		}

		static isMultiMeasuresSeriesSlot (slotAPI) {
			validateSlotAPI(slotAPI);
			const dataItemList = slotAPI.getDataItemList();
			return dataItemList.find(dataItem => {
				return dataItem.getColumnId() == SlotAPIUtil.MULTI_MEASURES_SERIES;
			}) ? true : false;
		}

		static isMultiMeasuresValueSlot (slotAPI) {
			return slotAPI.getDefinition().isMultiMeasureSupported() && slotAPI.getDataItemList().length > 1;
		}

		static isMultiMeasuresSeriesOrValue(slot, dataItemIdx) {
			const dataItemList = slot.getDataItemList();
			const dataItem = dataItemList.length >= dataItemIdx && dataItemList[dataItemIdx];
			return SlotAPIUtil.isMultiMeasuresSeriesOrValueDataItem(dataItem);
		}

		static isMultiMeasuresSeriesOrValueDataItem(dataItem) {
			return dataItem && [SlotAPIUtil.MULTI_MEASURES_SERIES, SlotAPIUtil.MULTI_MEASURES_VALUE].indexOf(dataItem.getColumnId()) !== -1;
		}

		static getMappedSlotListByDataset (visualization, dataset) {
			return visualization.getSlots().getMappedSlotList().filter(slot => slot.getDefinition().getDatasetIdList().includes(dataset));
		}

		static getDataSlotById (visualization, id) {
			const slots =  visualization.getSlots();
			return slots ? slots.getSlot(id) : null;
		}

		static getMetadataColumnForDataSlotHasOneMappedDataItem(dataSource, dataSlot, itemIndices) {
			const dataItemList = dataSlot.getDataItemList();

			//itemIndices can be [underfined | null | number | array ]
			let index = itemIndices || 0;
			if (Array.isArray(index)) {
				if (index.length  === 1) {
					index = index[0];
				} else {
					return null;
				}
			}

			return index < dataItemList.length ? dataSource.getMetadataColumn(dataItemList[index].getColumnId()) : null;
		}

		/**
		 *
		 * @param {*} slot slot api
		 * @return return all the data items in the slot except multiMeasure series
		 */
		static getValidDataItems(slot) {
			return slot.getDataItemList().filter(dataItem=>dataItem.getColumnId() !== SlotAPIUtil.MULTI_MEASURES_SERIES);
		}

		static isSlotSortable(slot, index, visualization) {
			index = index === undefined ? 0 : index;
			let def = slot.getDefinition();
			if (def && def.getProperty('sortable') !== undefined) {
				return def.getProperty('sortable');
			}

			if (slot.getDataItemList()[index].getType() === 'fact' && def && typeof def.getProperty('supportSortActionForMeasures') !== 'undefined'){
				return def.getProperty('supportSortActionForMeasures');
			}

			let slots = visualization.getDefinition().getProperty('dataSlots');
			for(let i = 0; i < slots.length; i++) {
				if (slots[i].subtype === 'location') {
					return false;
				}
			}

			if (def.getProperty('type') === 'category' || def.getProperty('type') === 'any') {
				return true;
			}

			let mappedSlots = visualization.getSlots().getMappedSlotList();
			if (mappedSlots.length === 2) {
				let hasType = (mappedSlots, sType) => {
					return (mappedSlots[0].getDefinition().getType() === sType || mappedSlots[1].getDefinition().getType() === sType);
				};
				return (hasType(mappedSlots, 'category') && hasType(mappedSlots, 'ordinal'));
			}

			return false;
		}

		static isSlotTopBottomable(visDefinition, dataSlot, itemIndex) {
			//DEFECT 253869
			//Verify if mapIndex is defined to differentiate when we're selecting specific data item that is a multi measure or multiple data items (get actions for y axis)
			const isMultiMeasureDataSlot = dataSlot && itemIndex == undefined ? SlotAPIUtil.isMultiMeasuresSeriesSlot(dataSlot) || SlotAPIUtil.isMultiMeasuresValueSlot(dataSlot) : false;
			if (isMultiMeasureDataSlot){
				return false;
			}

			itemIndex = itemIndex || 0;
			if(Array.isArray(itemIndex) && itemIndex.length != 1){
				return false;
			}

			const dataItem = dataSlot.getDataItemList()[itemIndex];
			if (!dataItem) {
				return false;	// when right click on an empty summary widget
			}

			const column = dataItem.getMetadataColumn();
			if (column === null) {
				return false;
			}

			const columnType = column.getType();
			const dataItemType = dataItem.getType();
			if (columnType === 'fact' || dataItemType === 'fact') {
				if (!visDefinition.getProperty('supportsNonContextualTopBottomAction')) {
					return false;
				}
			} else {
				if (!visDefinition.getProperty('supportsContextualTopBottomAction')) {
					return false;
				}
			}

			return !dataItem.getBinning() && column.getSourceCategory() !== 'property';
		}

		static getMultiMeasureSeriesSlot(visualization, dataset) {
			const slots = visualization.getSlots();
			return slots.getSlotList().find((slot) => {
				const definition = slot.getDefinition();
				const tags = definition && definition.getProperty('tags');
				return tags && tags.indexOf('ca.dashboard.repeatHere=true') > -1
					&& dataset === definition.getDatasetIdList()[0];
			});
		}

		static doesDataItemSupportBinning(slot, dataItem) {
			return slot.getDefinition().getType() !== 'ordinal' && dataItem.getMetadataColumn().getType() === 'fact';
		}
	}


	return SlotAPIUtil;
});
